import { ForwardedRef, forwardRef, useImperativeHandle, useRef, useState } from 'react';

import { useScopedTranslate } from '@locale/translate';

import { JsonHighlight } from './addons/json-highlight';
import { formatJsonTemplate, isJsonTemplateValid } from './addons/json-template';
import {
  FieldMappingTextEditorProps,
  JsonHighlightedFieldMappingTextEditor,
} from './field-mapping-text-editor';
import { EditorMethods } from './text-editor';

type EditorChangeValue = Parameters<NonNullable<FieldMappingTextEditorProps['onChange']>>[0];

export const FieldMappingJsonEditor = forwardRef(
  (
    { defaultValue, onChange, error, extensions, ...rest }: FieldMappingTextEditorProps,
    ref: ForwardedRef<EditorMethods>,
  ) => {
    const { t } = useScopedTranslate(locale);

    // A ref object (not a callback) — FieldMappingTextEditor reads editorRef.current
    // to insert variable pills, so the field-map "Add" needs a live .current.
    const editorRef = useRef<EditorMethods>(null);
    useImperativeHandle(
      ref,
      () => ({
        insertContent: (content) => editorRef.current?.insertContent(content),
        replaceContent: (content) => editorRef.current?.replaceContent(content),
      }),
      [],
    );

    // The Format button remounts the editor with the pretty-printed value
    // (bumping `remountKey`) — remounting reuses the load path, which keeps
    // whitespace and rebuilds variable pills. `overrideValue` then wins for the
    // life of this instance; parents remount per field, so a new field is a fresh
    // editor.
    const [overrideValue, setOverrideValue] = useState<EditorChangeValue | undefined>(undefined);
    const [remountKey, setRemountKey] = useState(0);
    const content = overrideValue ?? defaultValue;

    const latestValue = useRef<EditorChangeValue | undefined>(content);

    const [jsonError, setJsonError] = useState(() =>
      isJsonTemplateValid(content?.value ?? '') ? undefined : t('invalidJson'),
    );

    const handleChange: NonNullable<FieldMappingTextEditorProps['onChange']> = (value) => {
      latestValue.current = value;
      setJsonError(isJsonTemplateValid(value?.value ?? '') ? undefined : t('invalidJson'));
      onChange?.(value);
    };

    const handleFormat = () => {
      const current = latestValue.current;
      if (!current || typeof current.value !== 'string') {
        return;
      }
      const formatted = formatJsonTemplate(current.value);
      if (formatted === current.value) {
        return;
      }
      const next = { ...current, value: formatted };
      latestValue.current = next;
      setOverrideValue(next);
      setRemountKey((key) => key + 1);
      onChange?.(next);
    };

    return (
      <JsonHighlightedFieldMappingTextEditor
        key={remountKey}
        ref={editorRef}
        defaultValue={content}
        onChange={handleChange}
        onFormat={handleFormat}
        error={error ?? jsonError}
        fieldType="json"
        plaintext
        extensions={[JsonHighlight, ...(extensions ?? [])]}
        {...rest}
      />
    );
  },
);

const locale = {
  en: {
    invalidJson: 'Invalid JSON',
  },
};
